import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { setCountry, setLanguage as setReduxLanguage, setCurrency, setNumberSystem } from "../redux/settings/settingsSlice";
import languageArrow from "../assets/svg/language-arrow.svg";

const languages = [
  {
    code: "en-GB",
    name: "English",
    language: "(UK)",
    flag: "🇬🇧",
    currency: "£",
    currencyCode: "GBP",
    currencyName: "Pound Sterling",
    countryCode: "GB",
    numberSystem: "Western",
  },
  {
    code: "en-US",
    name: "English",
    language: "(US)",
    flag: "🇺🇸",
    currency: "$",
    currencyCode: "USD",
    currencyName: "US Dollar",
    countryCode: "US",
    numberSystem: "Western",
  },
  {
    code: "en-IN",
    name: "English",
    language: "(India)",
    flag: "🇮🇳",
    currency: "₹",
    currencyCode: "INR",
    currencyName: "Indian Rupee",
    countryCode: "IN",
    numberSystem: "Indian",
  },
  {
    code: "hi",
    name: "Hindi",
    language: "(हिंदी)",
    flag: "🇮🇳",
    currency: "₹",
    currencyCode: "INR",
    currencyName: "Indian Rupee",
    countryCode: "IN",
    numberSystem: "Indian",
  },
  {
    code: "es",
    name: "Spanish",
    language: "(Español)",
    flag: "🇪🇸",
    currency: "€",
    currencyCode: "EUR",
    currencyName: "Euro",
    countryCode: "ES",
    numberSystem: "Western",
  },
  {
    code: "fr",
    name: "French",
    language: "(Français)",
    flag: "🇫🇷",
    currency: "€",
    currencyCode: "EUR",
    currencyName: "Euro",
    countryCode: "FR",
    numberSystem: "Western",
  },
  {
    code: "ja",
    name: "Japanese",
    language: "(日本語)",
    flag: "🇯🇵",
    currency: "¥",
    currencyCode: "JPY",
    currencyName: "Japanese Yen",
    countryCode: "JP",
    numberSystem: "Western",
  },
  {
    code: "ar",
    name: "Arabic",
    language: "(العربية)",
    flag: "🇸🇦",
    currency: "﷼",
    currencyCode: "SAR",
    currencyName: "Saudi Riyal",
    countryCode: "SA",
    numberSystem: "Western",
  },
  {
    code: "ru",
    name: "Russian",
    language: "(Русский)",
    flag: "🇷🇺",
    currency: "₽",
    currencyCode: "RUB",
    currencyName: "Russian Ruble",
    countryCode: "RU",
    numberSystem: "Western",
  },
  {
    code: "pt",
    name: "Portuguese",
    language: "(Português)",
    flag: "🇧🇷",
    currency: "R$",
    currencyCode: "BRL",
    currencyName: "Brazilian Real",
    countryCode: "BR",
    numberSystem: "Western",
  },
  {
    code: "id",
    name: "Indonesian",
    language: "(Bahasa Indonesia)",
    flag: "🇮🇩",
    currency: "Rp",
    currencyCode: "IDR",
    currencyName: "Indonesian Rupiah",
    countryCode: "ID",
    numberSystem: "Western",
  },
  {
    code: "fa",
    name: "Persian",
    language: "(فارسی )",
    flag: "🇮🇷",
    currency: "﷼",
    currencyCode: "IRR",
    currencyName: "Iranian Rial",
    countryCode: "IR",
    numberSystem: "Western",
  },
  {
    code: "tr",
    name: "Turkish",
    language: "(Türkçe)",
    flag: "🇹🇷",
    currency: "₺",
    currencyCode: "TRY",
    currencyName: "Turkish Lira",
    countryCode: "TR",
    numberSystem: "Western",
  },
  {
    code: "ko",
    name: "Korean",
    language: "(한국어)",
    flag: "🇰🇷",
    currency: "₩",
    currencyCode: "KRW",
    currencyName: "South Korean Won",
    countryCode: "KR",
    numberSystem: "Western",
  },
  {
    code: "de",
    name: "German",
    language: "(Deutsch)",
    flag: "🇩🇪",
    currency: "€",
    currencyCode: "EUR",
    currencyName: "Euro",
    countryCode: "DE",
    numberSystem: "Western",
  },
  {
    code: "it",
    name: "Italian",
    language: "(italiano)",
    flag: "🇮🇹",
    currency: "€",
    currencyCode: "EUR",
    currencyName: "Euro",
    countryCode: "IT",
    numberSystem: "Western",
  },
  {
    code: "uk",
    name: "Ukrainian",
    language: "(Українська)",
    flag: "🇺🇦",
    currency: "₴",
    currencyCode: "UAH",
    currencyName: "Ukrainian Hryvnia",
    countryCode: "UA",
    numberSystem: "Western",
  },
  {
    code: "nl",
    name: "Netherlands",
    language: "(Dutch)",
    flag: "🇳🇱",
    currency: "€",
    currencyCode: "EUR",
    currencyName: "Euro",
    countryCode: "NL",
    numberSystem: "Western",
  },
  {
    code: "zh",
    name: "Chinese",
    language: "(中文)",
    flag: "🇨🇳",
    currency: "¥",
    currencyCode: "CNY",
    currencyName: "Chinese Yuan",
    countryCode: "CN",
    numberSystem: "Western",
  },
  {
    code: "cs",
    name: "Czech",
    language: "(Čeština)",
    flag: "🇨🇿",
    currency: "Kč",
    currencyCode: "CZK",
    currencyName: "Czech Koruna",
    countryCode: "CZ",
    numberSystem: "Western",
  },
  {
    code: "ur",
    name: "Urdu",
    language: "(اردو)",
    flag: "🇵🇰",
    currency: "₨",
    currencyCode: "PKR",
    currencyName: "Pakistani Rupee",
    countryCode: "PK",
    numberSystem: "Indian",
  },
  {
    code: "da",
    name: "Danish",
    language: "(dansk)",
    flag: "🇩🇰",
    currency: "kr",
    currencyCode: "DKK",
    currencyName: "Danish Krone",
    countryCode: "DK",
    numberSystem: "Western",
  },
  {
    code: "tl",
    name: "Filipino",
    language: "(Filipino)",
    flag: "🇵🇭",
    currency: "₱",
    currencyCode: "PHP",
    currencyName: "Philippine Peso",
    countryCode: "PH",
    numberSystem: "Western",
  },
  {
    code: "ms",
    name: "Malay",
    language: "(Bahasa Melayu)",
    flag: "🇲🇾",
    currency: "RM",
    currencyCode: "MYR",
    currencyName: "Malaysian Ringgit",
    countryCode: "MY",
    numberSystem: "Western",
  },
  {
    code: "fi",
    name: "Finnish",
    language: "(Suomi)",
    flag: "🇫🇮",
    currency: "€",
    currencyCode: "EUR",
    currencyName: "Euro",
    countryCode: "FI",
    numberSystem: "Western",
  },
  {
    code: "vi",
    name: "Vietnamese",
    language: "(Tiếng Việt)",
    flag: "🇻🇳",
    currency: "₫",
    currencyCode: "VND",
    currencyName: "Vietnamese Dong",
    countryCode: "VN",
    numberSystem: "Western",
  },
  {
    code: "th",
    name: "Thai",
    language: "(ไทย)",
    flag: "🇹🇭",
    currency: "฿",
    currencyCode: "THB",
    currencyName: "Thai Baht",
    countryCode: "TH",
    numberSystem: "Western",
  },
];

const currencyOptions = (() => {
  const map = new Map();
  languages.forEach((lang) => {
    const code = lang.currencyCode || lang.currency;
    if (!map.has(code)) {
      map.set(code, {
        code,
        symbol: lang.currency,
        name: lang.currencyName || lang.name,
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
})();

const currencyOptionMap = new Map(
  currencyOptions.map((option) => [option.code, option])
);

const getDefaultCurrencyOption = (languageCode) => {
  const language = languages.find((lang) => lang.code === languageCode);
  if (!language) {
    return currencyOptions[0];
  }
  const code = language.currencyCode || language.currency;
  return currencyOptionMap.get(code) || currencyOptions[0];
};

// Fetch user's location with multiple API fallbacks
const fetchUserLocation = async () => {
  // List of APIs to try in order
  const apis = [

    {
      name: "geojs.io",
      url: "https://get.geojs.io/v1/ip/geo.json",
      parser: (data) => ({
        countryCode: data.country_code,
        currency: null, // geojs doesn't provide currency, will use country code
      }),
    },
    {
      name: "ipapi.co",
      url: "https://ipapi.co/json/",
      parser: (data) => ({
        countryCode: data.country_code,
        currency: data.currency,
      }),
    },
  ];

  // Try each API with timeout
  for (const api of apis) {
    try {

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(api.url, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 429) {
          continue;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log("data", data);

      // Check if ip-api.com returned error
      if (api.name === "ip-api.com" && data.status === "fail") {
        console.warn(`⚠️ ${api.name} returned error, trying next API...`);
        continue;
      }

      const result = api.parser(data);

      // If currency is missing, get it from country code
      if (!result.currency && result.countryCode) {
        const language = languages.find(lang => lang.countryCode === result.countryCode);
        result.currency = language ? language.currencyCode : null;
        result.numberSystem = language ? language.numberSystem : "Western";
      }

      if (result.countryCode) {

        return result;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn(`⏱️ ${api.name} timeout, trying next API...`);
      } else {
        console.warn(`⚠️ ${api.name} failed:`, error.message);
      }
      continue;
    }
  }

  // All APIs failed, use browser language fallback
  console.warn("⚠️ All APIs failed, using browser language fallback...");
  return await fetchFallbackLocation();
};

// Fallback method using browser language
const fetchFallbackLocation = async () => {
  const browserLang = navigator.language || navigator.userLanguage;

  // Map browser language to country code
  const langToCountry = {
    'en-US': 'US',
    'en-GB': 'GB',
    'en-IN': 'IN',
    'hi': 'IN',
    'hi-IN': 'IN',
    'es': 'ES',
    'es-ES': 'ES',
    'fr': 'FR',
    'fr-FR': 'FR',
    'ja': 'JP',
    'ja-JP': 'JP',
    'ar': 'SA',
    'ru': 'RU',
    'pt': 'BR',
    'pt-BR': 'BR',
    'id': 'ID',
    'fa': 'IR',
    'tr': 'TR',
    'ko': 'KR',
    'de': 'DE',
    'it': 'IT',
    'uk': 'UA',
    'nl': 'NL',
    'zh': 'CN',
    'zh-CN': 'CN',
    'cs': 'CZ',
    'ur': 'PK',
    'da': 'DK',
    'tl': 'PH',
    'ms': 'MY',
    'fi': 'FI',
    'vi': 'VN',
    'th': 'TH',
  };

  const countryCode = langToCountry[browserLang] || langToCountry[browserLang.split('-')[0]] || 'US';

  // Get currency for the country
  const language = languages.find(lang => lang.countryCode === countryCode);
  const currency = language ? language.currencyCode : 'USD';


  return {
    countryCode,
    currency,
  };
};

// Map country code to preferred language
const getLanguageByCountry = (countryCode, currencyCode) => {

  if (countryCode === "IN") {
    const englishIndia = languages.find((lang) => lang.code === "en-IN");
    if (englishIndia) {
      return englishIndia;
    }
  }

  // Priority 1: Find exact country match with native language
  const nativeLanguage = languages.find(
    (lang) => lang.countryCode === countryCode && !lang.code.startsWith("en-")
  );
  if (nativeLanguage) {
    return nativeLanguage;
  }

  // Priority 2: Find English variant for that country
  const englishVariant = languages.find(
    (lang) => lang.countryCode === countryCode && lang.code.startsWith("en-")
  );
  if (englishVariant) {
    return englishVariant;
  }

  // Priority 3: Find any language with matching currency
  const currencyMatch = languages.find(
    (lang) => lang.currencyCode === currencyCode
  );
  if (currencyMatch) {
    return currencyMatch;
  }

  // Default to English (US)
  return languages.find((lang) => lang.code === "en-US") || languages[0];
};

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const reduxSettings = useSelector((state) => state.settings);

  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const containerRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];


  const selectedLanguage = useMemo(() => {
    return languages.find(lang => lang.code === reduxSettings.language.value);
  }, [reduxSettings.language.value]);

  // Get selected currency from Redux
  const selectedCurrency = currencyOptionMap.get(reduxSettings.currency.value) || currencyOptions[0];

  // Initialize with geolocation on first visit
  useEffect(() => {
    const initializeLocalization = async () => {

      // Check if this is the first visit
      const isFirstVisit = !sessionStorage.getItem("hasVisited");

      if (isFirstVisit) {
        sessionStorage.setItem("hasVisited", "true");

        // Fetch user location
        const locationData = await fetchUserLocation();

        if (locationData) {
          const { countryCode, currency } = locationData;

          // Determine best language for user's country
          const detectedLanguage = getLanguageByCountry(countryCode, currency);

          // Set language in i18n
          sessionStorage.setItem("lang", detectedLanguage.code);
          i18n.changeLanguage(detectedLanguage.code);

          // Dispatch to Redux (which also updates session storage)
          const country = languages.find(lang => lang.countryCode === countryCode);
          if (country) {
            dispatch(setCountry({
              value: country.countryCode,
              label: `${country.name} (${country.flag})`,
            }));
          }

          dispatch(setReduxLanguage({
            value: detectedLanguage.code,
            label: `${detectedLanguage.flag} ${detectedLanguage.name} ${detectedLanguage.language}`,
          }));

          dispatch(setCurrency({
            value: detectedLanguage.currencyCode,
            label: `${detectedLanguage.currencyCode}-${detectedLanguage.currencyName} (${detectedLanguage.currency})`,
          }));

          sessionStorage.setItem("currencySymbol", detectedLanguage.currency);
          sessionStorage.setItem("currencySource", "auto");
        } else {
          // Fallback to default if API fails
          const defaultLang = "en-US";
          const defaultLanguage = languages.find(lang => lang.code === defaultLang) || languages[0];
          const defaultCurrency = getDefaultCurrencyOption(defaultLang);

          sessionStorage.setItem("lang", defaultLang);
          i18n.changeLanguage(defaultLang);

          dispatch(setCountry({
            value: defaultLanguage.countryCode,
            label: `${defaultLanguage.name} (${defaultLanguage.flag})`,
          }));

          dispatch(setReduxLanguage({
            value: defaultLang,
            label: `${defaultLanguage.flag} ${defaultLanguage.name} ${defaultLanguage.language}`,
          }));

          dispatch(setCurrency({
            value: defaultCurrency.code,
            label: `${defaultCurrency.code}-${defaultCurrency.name} (${defaultCurrency.symbol})`,
          }));

          sessionStorage.setItem("currencySymbol", defaultCurrency.symbol);
          sessionStorage.setItem("currencySource", "auto");
        }
      } else {
        // Not first visit - use stored preferences from Redux/session storage
        const langFromStorage = sessionStorage.getItem("lang");

        if (langFromStorage && langFromStorage !== i18n.language) {
          i18n.changeLanguage(langFromStorage);
        }

        // Redux already initialized from session storage via the slice
      }

      setIsInitialized(true);
    };

    initializeLocalization();
  }, [dispatch, i18n]);

  // Remove lang and currency from URL if they exist
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasLang = params.has("lang");
    const hasCurrency = params.has("currency");

    if (hasLang || hasCurrency) {
      params.delete("lang");
      params.delete("currency");
      const queryString = params.toString();
      const newUrl = queryString
        ? `${window.location.pathname}?${queryString}`
        : window.location.pathname;
      window.history.replaceState(null, "", newUrl);
    }
  }, []);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsLanguageOpen(false);
        setIsCurrencyOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsLanguageOpen(false);
        setIsCurrencyOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!selectedCurrency || !isInitialized) {
      return;
    }
    sessionStorage.setItem("currencySymbol", selectedCurrency.symbol);
    const eventDetail = {
      code: selectedCurrency.code,
      symbol: selectedCurrency.symbol,
      name: selectedCurrency.name,
    };
    window.dispatchEvent(
      new CustomEvent("currencyChange", { detail: eventDetail })
    );
  }, [selectedCurrency, isInitialized]);

  const changeLanguage = (languageCode) => {
    const selectedLang = languages.find(lang => lang.code === languageCode);
    if (!selectedLang) return;

    sessionStorage.setItem("lang", languageCode);
    i18n.changeLanguage(languageCode);

    // Update Redux
    dispatch(setReduxLanguage({
      value: selectedLang.code,
      label: `${selectedLang.flag} ${selectedLang.name} ${selectedLang.language}`,
    }));

    setIsLanguageOpen(false);
    setIsCurrencyOpen(false);
  };

  const changeCurrency = (currencyCode) => {
    if (!currencyOptionMap.has(currencyCode)) {
      return;
    }
    const selectedCurr = currencyOptionMap.get(currencyCode);

    sessionStorage.setItem("currencySource", "manual");

    // Update Redux
    dispatch(setCurrency({
      value: selectedCurr.code,
      label: `${selectedCurr.code}-${selectedCurr.name} (${selectedCurr.symbol})`,
    }));

    setIsCurrencyOpen(false);
  };

  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  if (!isInitialized) {
    return null;
  }


  return (
    <div
      className={`absolute top-1/2 -translate-y-1/2 flex sm:gap-[6px] items-center ${isRTL() ? "left-1 flex-row-reverse" : "right-1"
        }`}
      ref={containerRef}
    >
      <div className="relative">
        <button
          type="button"
          className={`flex  items-center  text-sm w-fit  font-medium text-gray-700 bg-whiterounded-md hover:bg-gray-50 focus:outline-none ${isRTL() ? "justify-start" : "justify-end"}`}
          onClick={() => {
            setIsLanguageOpen((prev) => !prev);
            setIsCurrencyOpen(false);
          }}
          aria-expanded={isLanguageOpen}
          aria-haspopup="listbox"
        >
          {/* <span className="text-xl mx-[6px]">{selectedLanguage.flag}</span> */}
          <img src={`https://flagsapi.com/${selectedLanguage.countryCode}/flat/32.png`} alt={selectedLanguage.name} className={`w-6 h-6 ${isRTL() ? "ml-3" : "mr-3"}`} />
          <div className="flex flex-col items-start">
            <span className="text-[16px] font-lato-regular-400">{selectedLanguage.name}</span>
          </div>
          <img
            src={languageArrow}
            alt="Toggle language menu"
            className={`mx-[6px] transition-transform ${isLanguageOpen ? "rotate-180" : "rotate-0"
              }`}
          />
        </button>

        {isLanguageOpen && (
          <div
            className={`absolute !z-[9999] mt-2 max-h-40 w-52 sm:w-56 overflow-y-auto rounded-[10px] bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${isRTL() ? "left-0" : "right-0"
              }`}
            role="listbox"
          >
            {languages.map((language) => (
              <button
                type="button"
                key={language.code}
                className={`flex w-full items-center  px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 ${language.code === reduxSettings.language.value
                  ? "bg-light-gray hover:bg-light-gray"
                  : ""
                  }`}
                onClick={() => changeLanguage(language.code)}
              >
                {/* <span className="mr-3 text-lg">{language.flag}</span> */}
                <img src={`https://flagsapi.com/${language.countryCode}/flat/32.png`} alt={selectedLanguage.name} className={`w-6 h-6 ${isRTL() ? "ml-3" : "mr-3"}`} />
                <div className="flex flex-col">
                  <div className="flex items-center gap-[2px]"> <span className="font-medium">{language.name}</span>
                    <span className="text-xs text-gray-500">
                      {language.language}
                    </span></div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative">
        <button
          type="button"
          className={`flex w-fit  items-center  px-0  sm:px-3  text-left text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none ${isRTL() ? "justify-end" : "justify-start"}`}
          onClick={() => {
            setIsCurrencyOpen((prev) => !prev);
            setIsLanguageOpen(false);
          }}
          aria-expanded={isCurrencyOpen}
          aria-haspopup="listbox"
        >
          <span className="sm:text-lg text-base mx-[6px]">{selectedCurrency.symbol}</span>
          <div className="flex flex-col items-start">
            <span className="">{selectedCurrency.code}</span>
          </div>
          <img
            src={languageArrow}
            alt="Toggle currency menu"
            className={`mx-[6px] transition-transform ${isCurrencyOpen ? "rotate-180" : "rotate-0"
              }`}
          />
        </button>

        {isCurrencyOpen && (
          <div
            className={`absolute z-10 mt-2 max-h-40 w-56 overflow-y-auto rounded-[10px] bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${isRTL() ? "left-0" : "right-0"
              }`}
            role="listbox"
          >
            {currencyOptions.map((currency) => (
              <button
                type="button"
                key={currency.code}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 ${currency.code === selectedCurrency.code
                  ? "bg-light-gray hover:bg-light-gray"
                  : ""
                  }`}
                onClick={() => changeCurrency(currency.code)}
              >
                <div className="flex justify-between w-full items-center">
                  <div className="flex gap-1 items-center">
                    <span className="font-medium">{currency.code}</span>
                    <span className="text-xs text-gray-500">({currency.name})</span>
                  </div>
                  <div>
                    <span className="text-lg">{currency.symbol}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageSelector;